import { ErrorHandler, Injectable, inject } from '@angular/core';
import {
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpInterceptor,
  HttpErrorResponse
} from '@angular/common/http';
import { EMPTY, Observable, catchError, tap, throwError } from 'rxjs';
import { Router } from '@angular/router';
import { STORAGE_KEYS } from '../constants/storage-keys.const';
import { AuthService } from '../services/auth.service';
import { EventStateService } from '../services/state-service/event-state.service';
import {
  ERROR_TOAST_DEDUPE_MS,
  ErrorToastService
} from '../services/error-toast.service';
import { UtilityService } from '../services/utility.service';
import { PostMessageTypesEnum } from '../enums/post-message-types.enum';
import { toReportableError } from '../utils/http-error.util';
import {
  EXPECTED_HTTP_ERROR,
  LOCALLY_HANDLED_HTTP_ERROR
} from '../constants/http-context.const';

@Injectable()
export class TraceabilityInterceptor implements HttpInterceptor {
  private readonly router = inject(Router);
  private readonly errorHandler = inject(ErrorHandler);
  private readonly authService = inject(AuthService);
  private readonly eventStateService = inject(EventStateService);
  private readonly errorToastService = inject(ErrorToastService);
  private readonly utilityService = inject(UtilityService);

  private sessionEndedAt = -Infinity;

  intercept(
    request: HttpRequest<unknown>,
    next: HttpHandler
  ): Observable<HttpEvent<unknown>> {
    let modifiedRequest: HttpRequest<unknown>;

    if (request.headers.has('no-auth')) {
      modifiedRequest = request.clone({
        headers: request.headers.delete('no-auth')
      });
    } else {
      const isOffPlatform =
        window.location.pathname.includes('/offplatformsiview');
      const authToken = isOffPlatform
        ? this.authService.offPlatformSecretKey || ''
        : `Token ${this.authService.userInfoToken}`;

      modifiedRequest = request.clone({
        headers: request.headers.set('Authorization', authToken)
      });
    }
    return next.handle(modifiedRequest).pipe(
      tap(() => {
        this.eventStateService.error = null;
      }),
      catchError((error: HttpErrorResponse) => {
        this.eventStateService.error = error;
        this.eventStateService.resetAllLoadingStates();
        this.eventStateService.downloadEuisLoading = false;
        this.eventStateService.downloadFullRiskLoading = false;
        if (request.context.get(LOCALLY_HANDLED_HTTP_ERROR)(error)) {
          return throwError(() => error);
        }
        const reportedError = toReportableError(error);
        const body: unknown = error.error;
        const errors =
          body && typeof body === 'object' && 'errors' in body
            ? body.errors
            : undefined;
        const firstError: unknown = Array.isArray(errors)
          ? errors[0]
          : undefined;
        if (
          firstError &&
          typeof firstError === 'object' &&
          'detail' in firstError &&
          firstError.detail === 'provider AgriTrace not supported'
        ) {
          return throwError(() => reportedError);
        }

        // Authentication failures end the session instead of showing an error.
        if (error.status === 401) {
          this.endSession();
          return EMPTY;
        }

        if (error.status === 404 && error.message === 'Not found') {
          this.showErrorPage(404);
          sessionStorage.removeItem(STORAGE_KEYS.ARP_TOKEN);
        } else {
          this.errorToastService.showHttpError(error, request.method);
        }

        if (request.context.get(EXPECTED_HTTP_ERROR)(error)) {
          return EMPTY;
        }

        return throwError(() => reportedError);
      })
    );
  }

  private endSession() {
    sessionStorage.removeItem(STORAGE_KEYS.ARP_TOKEN);
    // A burst of failed requests should end the session once.
    const now = Date.now();
    if (now - this.sessionEndedAt < ERROR_TOAST_DEDUPE_MS) return;
    this.sessionEndedAt = now;
    // HCP handles this like its own 401 and takes the user to sign-in.
    this.utilityService.emitPropToParent(
      'type',
      PostMessageTypesEnum.SESSION_EXPIRED
    );
    this.showErrorPage(401);
  }

  private showErrorPage(status: number) {
    this.router
      .navigate(['error'], { queryParams: { status } })
      .catch((navigationError: unknown) => {
        this.errorHandler.handleError(navigationError);
      });
  }
}
